import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { getMyCredits } from "@/lib/quota.functions";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Sparkles, Check, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { createCheckoutSession } from "@/utils/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { arePaymentsAvailable } from "@/lib/native-commerce-flags";
import { useCommercialConfigLoad } from "@/lib/pricing-labels";
import { canStartCheckout } from "@/lib/commercial-config-load";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/_authenticated/credits")({
  component: CreditsPage,
  errorComponent: ({ error, reset }) => (
    <div className="container max-w-2xl mx-auto py-12 px-4 text-center">
      <h1 className="text-xl font-semibold mb-2">Não foi possível carregar os packs</h1>
      <p className="text-sm text-muted-foreground mb-4">{error instanceof Error ? error.message : "Erro desconhecido"}</p>
      <Button
        onClick={() => {
          reset();
          window.location.reload();
        }}
      >
        Tentar novamente
      </Button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container max-w-2xl mx-auto py-12 px-4 text-center">
      <p className="text-sm text-muted-foreground">Página não encontrada.</p>
    </div>
  ),
});

type Pack = { id: string; credits: number; popular?: boolean };

const PACKS: Pack[] = [
  { id: "credits_pack_10", credits: 10 },
  { id: "credits_pack_30", credits: 30, popular: true },
  { id: "credits_pack_100", credits: 100 },
];

const PACK_IDS = PACKS.map((p) => p.id);

const COPY = {
  pt: {
    title: "Créditos de itinerários",
    subtitle: "Compra packs para gerar mais itinerários. Os créditos nunca expiram.",
    balance: "Saldo atual",
    credits: "créditos",
    buy: "Comprar",
    popular: "Mais popular",
    perks: ["Sem subscrição", "Sem expiração", "Detalhes premium incluídos"],
    back: "Voltar",
    loadError: "Não foi possível carregar os preços dos packs.",
    retry: "Tentar novamente",
    stale: "A mostrar os últimos preços conhecidos. Confirma antes de comprar.",
    unavailable: "Indisponível",
  },
  en: {
    title: "Itinerary credits",
    subtitle: "Buy packs to generate more itineraries. Credits never expire.",
    balance: "Current balance",
    credits: "credits",
    buy: "Buy",
    popular: "Most popular",
    perks: ["No subscription", "No expiry", "Premium stop details included"],
    back: "Back",
    loadError: "We couldn't load the pack prices.",
    retry: "Try again",
    stale: "Showing the last known prices. Please confirm before buying.",
    unavailable: "Unavailable",
  },
};

function CreditsPage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fetchCredits = useServerFn(getMyCredits);
  const createCheckout = useServerFn(createCheckoutSession);
  const config = useCommercialConfigLoad(PACK_IDS);
  const pricing = config.config?.pricing ?? {};
  const [balance, setBalance] = useState<number>(0);
  const [openingPack, setOpeningPack] = useState<string | null>(null);
  const t = COPY[lang as "pt" | "en"] ?? COPY.en;

  useEffect(() => {
    fetchCredits({})
      .then((r) => setBalance(r.balance))
      .catch(() => {});
  }, [fetchCredits]);

  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`credits:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "itinerary_credits",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchCredits({})
            .then((r) => setBalance(r.balance))
            .catch(() => {});
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, fetchCredits]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("purchased") !== "1") return;

    console.info("[credits-checkout] returned from Stripe, refreshing balance");
    let cancelled = false;
    let attempts = 0;
    const refreshBalance = () => {
      fetchCredits({})
        .then((r) => {
          if (!cancelled) setBalance(r.balance);
        })
        .catch((error) => console.error("[credits-checkout] balance refresh failed", error));
    };
    const interval = window.setInterval(() => {
      attempts += 1;
      refreshBalance();
      if (attempts >= 10) window.clearInterval(interval);
    }, 1500);
    refreshBalance();

    params.delete("purchased");
    params.delete("session_id");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [fetchCredits]);

  async function buyPack(priceId: string) {
    if (!user || openingPack) return;
    if (!arePaymentsAvailable()) {
      alert("As compras não estão disponíveis nesta versão da aplicação.");
      return;
    }
    if (!canStartCheckout(config, priceId)) {
      alert(t.loadError);
      return;
    }
    setOpeningPack(priceId);
    console.info("[credits-checkout] opening hosted checkout", {
      priceId,
      pwa: window.matchMedia("(display-mode: standalone)").matches,
    });
    try {
      const result = await createCheckout({
        data: {
          priceId,
          customerEmail: user.email,
          userId: user.id,
          returnUrl: `${window.location.origin}/credits?purchased=1&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/credits?checkout=cancelled`,
          environment: getStripeEnvironment(),
          uiMode: "hosted",
        },
      });
      if ("error" in result) throw new Error(result.error);
      if (!result.url) throw new Error("Stripe não devolveu o link de pagamento");
      window.location.assign(result.url);
    } catch (error) {
      console.error("[credits-checkout] failed", error);
      setOpeningPack(null);
      alert(error instanceof Error ? error.message : "Não foi possível abrir o pagamento");
    }
  }

  return (
    <div className="container max-w-5xl mx-auto py-8 px-4">
      <Button variant="ghost" onClick={() => navigate({ to: "/" })} className="mb-4 gap-2">
        <ArrowLeft className="w-4 h-4" /> {t.back}
      </Button>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">{t.title}</h1>
        <p className="text-muted-foreground">{t.subtitle}</p>
      </div>

      <Card className="mb-8 bg-gradient-to-br from-primary/10 to-primary/5">
        <CardContent className="py-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t.balance}</p>
            <p className="text-3xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              {balance}{" "}
              <span className="text-base font-normal text-muted-foreground">{t.credits}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {config.status === "loading" ? (
        <div className="grid md:grid-cols-3 gap-4" data-testid="packs-skeleton">
          {PACKS.map((pack) => (
            <Card key={pack.id}>
              <CardHeader className="space-y-3">
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-9 w-24" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
              </CardContent>
              <CardFooter>
                <Skeleton className="h-10 w-full" />
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : config.status === "error" ? (
        <Card className="text-center" data-testid="packs-error">
          <CardContent className="py-10 space-y-4">
            <p className="text-sm text-muted-foreground">{config.error ?? t.loadError}</p>
            <Button onClick={config.reload} className="gap-2">
              <RefreshCw className="h-4 w-4" /> {t.retry}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {config.status === "fallback" ? (
            <div className="mb-4 flex flex-col items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 sm:flex-row sm:justify-between">
              <span>{t.stale}</span>
              <Button size="sm" variant="outline" onClick={config.reload} className="gap-2">
                <RefreshCw className="h-4 w-4" /> {t.retry}
              </Button>
            </div>
          ) : null}
          <div className="grid md:grid-cols-3 gap-4" data-testid="packs-grid">
            {PACKS.map((pack) => {
              const buyable = canStartCheckout(config, pack.id);
              return (
                <Card
                  key={pack.id}
                  className={pack.popular ? "border-primary shadow-lg relative" : ""}
                >
                  {pack.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full">
                      {t.popular}
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-2xl">
                      {pack.credits}{" "}
                      <span className="text-base font-normal text-muted-foreground">
                        {t.credits}
                      </span>
                    </CardTitle>
                    <CardDescription className="text-3xl font-bold text-foreground pt-2">
                      {pricing[pack.id]?.label ?? t.unavailable}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm">
                      {t.perks.map((p) => (
                        <li key={p} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-primary" /> {p}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button
                      onClick={() => buyPack(pack.id)}
                      disabled={Boolean(openingPack) || !buyable}
                      className="w-full gap-2"
                    >
                      {openingPack === pack.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : null}
                      {buyable ? t.buy : t.unavailable}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
